import { Text, View, StyleSheet, Svg, Rect, Path } from "@react-pdf/renderer";
import moment from "moment";
import React from "react";

const C = {
  navy: "#000000",
  navyMid: "#000000",
  border: "#CBD5E1",
  rowAlt: "#F8FAFC",
  headerBg: "#F1F5F9",
  headerText: "#000000",
  teal: "#000000",
  white: "#FFFFFF",
  offWhite: "#F8FAFC",
  text: "#000000",
  textMuted: "#000000",
  labelBg: "#F1F5F9",
};

const CheckboxIcon = ({ checked }) => {
  if (checked) {
    return (
      <Svg width={9} height={9} viewBox="0 0 12 12">
        <Path
          d="M2.5 6L5 8.5L9.5 3.5"
          stroke="#000000"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </Svg>
    );
  }
  return null;
};

const styles = StyleSheet.create({
  main: {
    paddingHorizontal: 14,
    paddingTop: 4,
    paddingBottom: 10,
    backgroundColor: C.white,
  },

  // Hidden ID — white text, stays first for parsing
  cargoquery: {
    color: C.white,
    textAlign: "right",
    fontSize: 5,
    marginBottom: 2,
  },

  // ─── Section Header ───────────────────────────────────
  sectionTitle: {
    fontSize: 6,
    fontFamily: "Helvetica",
    color: C.navy,
    marginBottom: 3,
  },

  // ─── Cargo Operations Table ───────────────────────────
  tableWrapper: {
    borderWidth: 0.75,
    borderColor: C.border,
    overflow: "hidden",
    marginBottom: 5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: C.border,
    alignItems: "stretch",
    minHeight: 14,
  },
  tableCellHeaderWrapper: {
    backgroundColor: C.headerBg,
    paddingVertical: 3,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  tableCellHeader: {
    color: C.headerText,
    fontSize: 6,
    fontFamily: "Helvetica",
    textAlign: "center",
  },
  tableCellWrapper: {
    paddingVertical: 2.5,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    backgroundColor: C.white,
  },
  tableCellWrapperAlt: {
    paddingVertical: 2.5,
    paddingHorizontal: 2,
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    backgroundColor: C.rowAlt,
  },
  tableCell: {
    color: C.text,
    fontSize: 6,
    fontFamily: "Helvetica",
    textAlign: "center",
  },

  // ─── Split Configuration & Remarks Row ────────────────
  splitContainer: {
    position: "relative",
    marginTop: 2,
  },
  leftCol: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "48.5%",
  },
  rightCol: {
    width: "50.5%",
    marginLeft: "49.5%",
  },
  configCard: {
    width: "100%",
    borderWidth: 0.75,
    borderColor: C.border,
    backgroundColor: C.offWhite,
    padding: 7,
  },
  remarksCard: {
    width: "100%",
    borderWidth: 0.75,
    borderColor: C.border,
    backgroundColor: C.offWhite,
    padding: 7,
  },
  cardTitle: {
    fontSize: 6,
    fontFamily: "Helvetica",
    color: C.navy,
    borderBottomWidth: 0.5,
    borderBottomColor: C.border,
    paddingBottom: 4,
    marginBottom: 5,
  },

  // Form Fields inside Configuration
  fieldGroup: {
    marginBottom: 4,
  },
  fieldLabel: {
    fontSize: 6,
    fontFamily: "Helvetica",
    color: C.navy,
    marginBottom: 2,
  },
  fieldValueBox: {
    backgroundColor: C.white,
    borderWidth: 0.5,
    borderColor: C.border,
    paddingVertical: 3,
    paddingHorizontal: 5,
    fontSize: 6,
    fontFamily: "Helvetica",
    color: C.text,
  },
  gridRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  gridCol: {
    width: "48%",
  },

  // Checkboxes
  checkboxRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 4,
  },
  checkboxItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 12,
  },
  checkboxLabel: {
    fontSize: 6,
    fontFamily: "Helvetica",
    color: C.text,
    marginLeft: 4,
  },

  // Remarks / Notes Box
  remarksBox: {
    backgroundColor: C.white,
    borderWidth: 0.5,
    borderColor: C.border,
    padding: 6,
  },
  remarksText: {
    fontSize: 6,
    fontFamily: "Helvetica",
    color: C.text,
    lineHeight: 1.35,
  },
});

function CustomTablePDF(props) {
  const { fields = [], data = [], query = {}, pdfSaveId = "" } = props;

  const filteredFields = fields.filter((field) => !field?.hide);
  const tabledata = data.filter(
    (field) =>
      field?.operation === "Loading" || field?.operation === "Discharging"
  );

  return (
    <View style={styles.main}>
      {/* Hidden ID — white text, first in DOM for parsing */}
      <Text style={styles.cargoquery}>cargoqueryid-{pdfSaveId}#</Text>

      {/* ── Cargo Operations ── */}
      <Text style={styles.sectionTitle}>Cargo Operations</Text>

      <View style={styles.tableWrapper}>
        {/* Header Row */}
        <View style={styles.tableRow}>
          {filteredFields.map((_item, _idx) => (
            <View
              key={_idx}
              style={[
                styles.tableCellHeaderWrapper,
                {
                  width: `${_item.width}%`,
                  borderRightWidth: _idx !== filteredFields.length - 1 ? 0.5 : 0,
                  borderRightColor: C.border,
                },
              ]}
            >
              <Text style={styles.tableCellHeader} maxLines={1}>
                {_item.title}
              </Text>
            </View>
          ))}
        </View>

        {/* Body Rows */}
        {tabledata.map((item, idx) => {
          if (!item) return null;
          const isAlt = idx % 2 === 1;
          const cellWrapperStyle = isAlt ? styles.tableCellWrapperAlt : styles.tableCellWrapper;

          return (
            <View key={idx} style={styles.tableRow}>
              {filteredFields.map((_item, _idx) => {
                const val = item[_item.value];
                const value_alt = _item.value_alt ? item[_item.value_alt] : "";
                const displayValue = val !== "" && val !== null && val !== undefined ? val : (value_alt ? value_alt : "—");

                return (
                  <View
                    key={_idx}
                    style={[
                      cellWrapperStyle,
                      {
                        width: `${_item.width}%`,
                        borderRightWidth: _idx !== filteredFields.length - 1 ? 0.5 : 0,
                        borderRightColor: C.border,
                      },
                    ]}
                  >
                    {_item.custom ? (
                      <Text style={[styles.tableCell, item.style ? item.style : {}]}>
                        {_item.component(item)}
                      </Text>
                    ) : (
                      <Text
                        style={[styles.tableCell, item.style ? item.style : {}]}
                        maxLines={1}
                      >
                        {displayValue}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>

      {/* ── Split Configuration & Remarks Panels ── */}
      <View style={styles.splitContainer}>
        {/* Left Column: Configuration */}
        <View style={styles.leftCol}>
          <View style={styles.configCard}>
            <Text style={styles.cardTitle}>Configuration</Text>

            {/* Name */}
            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>Name</Text>
              <Text style={styles.fieldValueBox}>{query?.name || "—"}</Text>
            </View>

            {/* Freight Mode & Qty Option */}
            <View style={styles.gridRow}>
              <View style={styles.gridCol}>
                <Text style={styles.fieldLabel}>Freight Mode</Text>
                <Text style={styles.fieldValueBox}>{query?.freightMode || "Voyage (Single)"}</Text>
              </View>
              <View style={styles.gridCol}>
                <Text style={styles.fieldLabel}>Qty Option</Text>
                <Text style={styles.fieldValueBox}>{query?.qtyMode || "MOLOO"}</Text>
              </View>
            </View>

            {/* Laycan Start & Laycan End */}
            <View style={styles.gridRow}>
              <View style={styles.gridCol}>
                <Text style={styles.fieldLabel}>Laycan Start</Text>
                <Text style={styles.fieldValueBox}>
                  {query?.laycanStartDate
                    ? moment(query.laycanStartDate).format("DD-MM-YY hh:mm A")
                    : "—"}
                </Text>
              </View>
              <View style={styles.gridCol}>
                <Text style={styles.fieldLabel}>Laycan End</Text>
                <Text style={styles.fieldValueBox}>
                  {query?.laycanEndDate
                    ? moment(query.laycanEndDate).format("DD-MM-YY hh:mm A")
                    : "—"}
                </Text>
              </View>
            </View>

            {/* Checkboxes */}
            <View style={styles.checkboxRow}>
              <View style={styles.checkboxItem}>
                <CheckboxIcon checked={Boolean(query?.suez)} />
                <Text style={styles.checkboxLabel}>Avoid Suez Canal</Text>
              </View>
              <View style={styles.checkboxItem}>
                <CheckboxIcon checked={Boolean(query?.panama)} />
                <Text style={styles.checkboxLabel}>Avoid Panama Canal</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Right Column: Remarks / Notes */}
        <View style={styles.rightCol}>
          <View style={styles.remarksCard}>
            <Text style={styles.cardTitle}>Remarks / Notes</Text>
            <View style={styles.remarksBox}>
              <Text style={styles.remarksText}>
                {query?.message || "No remarks or notes provided."}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

export default CustomTablePDF;